// External libraries
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Components
import { Body, BodyColor, BodyType, CustomDropdown, Heading, SpinnerLite, TypographyColor, TypographyType } from '../../ui-elements';
import { BranchGraph } from '.';
import NoPoi from '../../pages/NoPoi';
import { TableSkeletonLoader } from '../home/TableSkeleton';

// Utilities
import { useDashboardService } from '../../services';
import { useHandleError } from '../../helpers';
import { geoState, shimmerLiteState, spinnerLiteState, spinnerState, selectedCardState } from '../../states';


export const MyBusinessPresence = ({ isLoading, setIsLoading }: any) => {
    const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
    const dashboardService = useDashboardService();
    const { getErrorMsg } = useHandleError();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const selectedGeo = useRecoilValue(geoState);
    const [spinnerLite, setSpinnerLite] = useRecoilState(spinnerLiteState);
    const [shimmerLite, setShimmerLite] = useRecoilState(shimmerLiteState);
    const [selectedPresenceType, setSelectedPresenceType] = useState('');
    const [selectedPresenceTypeIndex, setSelectedPresenceTypeIndex] = useState(0);
    const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(0); // Default to 0th row expanded
    const [dropDownData, setDropDownData] = useState<any[]>([]);
    const [selectedfilter, setSelectedfilter] = useState<string | null>(null);
    const [mbptableData, setMbptableData] = useState<any>(null);
    const [selectedRowData, setSelectedRowData] = useState(mbptableData?.find((item: any) => (item.presence_type == selectedPresenceType))?.presence_data?.[0]);
    const [graphData, setGraphData] = useState<any>({});
    const [sorting, setSorting] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' } | null>(null);
    const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(0);
    const [isPresenceType, setIsPresenceType] = useState(false);
    const [isfilterChange, setIsfilterChange] = useState(false);
    const selectedCardType = useRecoilValue(selectedCardState);
    const [childSelectedRowIndex, setChildSelectedRowIndex] = useState(-1);
    const [tooltip, setTooltip] = useState({ show: false, text: "", x: 0, y: 0 });

    // Helper function to check if text is truncated
    const isTextTruncated = (element: HTMLElement | null): boolean => {
        if (!element) return false;
        return element.scrollWidth > element.clientWidth;
    };

    // Updated tooltip handler that checks for truncation
    const handleMouseEnter = (e: React.MouseEvent<HTMLTableCellElement>, text: string) => {
        const textElement = e.currentTarget.querySelector('.ellipsis-text, .text-truncate');
        if (textElement && isTextTruncated(textElement as HTMLElement)) {
            const rect = e.currentTarget.getBoundingClientRect();
            setTooltip({
                show: true,
                text: text,
                x: rect.left + 50,
                y: rect.top + 40
            });
        }
    };

    const handleMouseLeave = () => {
        setTooltip({ show: false, text: "", x: 0, y: 0 });
    };

    const handleCatchmentClick = (catchmentId: number) => {
        const params = new URLSearchParams(searchParams);
        params.set('catchment', catchmentId.toString());
        navigate(`?${params.toString()}`);
    };

    useEffect(() => {
        if (
            selectedRowIndex !== null &&
            rowRefs.current[selectedRowIndex]
        ) {
            rowRefs?.current[selectedRowIndex]?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, [selectedRowIndex, mbptableData]);

    const toggleExpand = (index: number, row: any) => {
        if (expandedRowIndex !== index) {
            setSelectedRowData(row);
        }
        setExpandedRowIndex(prevIndex => (prevIndex === index ? null : index));
    };

    const handlePresenceTypeChange = (item: any, index: number) => {
        setIsPresenceType(true);
        setSelectedPresenceType(item.presence_type);
        setSelectedPresenceTypeIndex(index);
        setSelectedRowIndex(0);
    }

    const handleSelectedRow = () => {
        if (!mbptableData || mbptableData.length === 0) return;

        const presenceData = mbptableData?.find((item: any) => (item.presence_type == selectedPresenceType))?.presence_data || [];
        let foundIndex = -1;
        let foundChildIndex = -1;

        if (selectedRowData && selectedRowData.presence_id) {
            // Try to find in parent rows
            foundIndex = presenceData.findIndex(
                (item: any) => item.presence_id === selectedRowData.presence_id
            );
            // If not found in parent, try to find in children
            if (foundIndex === -1) {
                presenceData.forEach((item: any, idx: number) => {
                    if (Array.isArray(item.associated_presence)) {
                        const childIdx = item.associated_presence.findIndex(
                            (child: any) => child.presence_id === selectedRowData.presence_id
                        );
                        if (childIdx !== -1) {
                            foundIndex = idx;
                            foundChildIndex = childIdx;
                        }
                    }
                });
            }
        }

        if (foundIndex !== -1) {
            // Found parent or child
            if (foundChildIndex !== -1) {
                setSelectedRowData(presenceData[foundIndex].associated_presence[foundChildIndex]);
                setSelectedRowIndex(foundIndex);
                setChildSelectedRowIndex(foundChildIndex);
            } else {
                setSelectedRowData(presenceData[foundIndex]);
                setSelectedRowIndex(foundIndex);
                setChildSelectedRowIndex(-1);
            }
        } else {
            // Default to first row
            setSelectedRowData(presenceData[0]);
            setSelectedRowIndex(0);
            setChildSelectedRowIndex(-1);
        }
    }

    const geoCode = useMemo(() => {
        const { country, state, district, sub_district, village } = selectedGeo;
        const geo_id = village || sub_district || district || state || country;
        return (geo_id);
    }, [selectedGeo]);

    const [isBarGraphLoading, setIsBarGraphLoading] = useState(true);
    const [isGraphDataLoading, setIsGraphDataLoading] = useState(true);
    const fetchDropDownData = async () => {
        setIsBarGraphLoading(true);
        setIsLoading(true)
        setSpinnerLite(prev => prev + 1);
        try {
            const response = await dashboardService.getFilteredDropdown(geoCode);
            if (response && Array.isArray(response)) {
                setDropDownData(response);
                setSelectedfilter(response.length > 0 ? response[0] : null);
            }
        } catch (error) {
            getErrorMsg(error);
        } finally {
            setSpinnerLite(prev => prev - 1);
            setIsBarGraphLoading(false);
            setIsLoading(false)
        }
    };

    const fetchMbpTableData = async () => {
        setShimmerLite((prev: string[]) => [...prev, "mbpTableData"]);
        setShimmerLite((prev: string[]) => prev.filter(item => item !== "dataIsLoading"));
        setIsBarGraphLoading(true);
        setIsLoading(true)
        setSpinnerLite(prev => prev + 1);
        setMbptableData(null);
        let param = '';
        let paramclass = '';
        if (selectedfilter) {
            [param, paramclass] = selectedfilter.split(' by ');
        }
        try {
            const response = await dashboardService.getMBPTableData(geoCode, param, paramclass);
            if (response?.length) {
                setMbptableData(response);

                const isSelectedValid = response.some(
                    (item: any) => item.presence_type === selectedPresenceType
                );

                setSelectedPresenceType(
                    selectedPresenceType && isSelectedValid
                        ? selectedPresenceType
                        : response[0]?.presence_type
                );
            }

        } catch (error) {
            getErrorMsg(error);
        } finally {
            setSpinnerLite(prev => prev - 1);
            setShimmerLite((prev: string[]) => prev.filter(item => item !== "mbpTableData"));
            setIsBarGraphLoading(false);
            setIsLoading(false)

        }
    };

    const fetchGraphData = async () => {
        try {
            const response = await dashboardService.getMBPGraphData({
                geo_id: geoCode,
                param: selectedfilter,
                presence_type: mbptableData?.find((item: any) => (item.presence_type == selectedPresenceType))?.presence_type,
                presence_id: selectedRowData.presence_id
            });
            if (response) {
                setGraphData(response);
            } else {
                setGraphData({});
            }
        } catch (error) {
            getErrorMsg(error);
            setGraphData({});
        } finally {
            setShimmerLite((prev: string[]) => prev.filter(item => item !== "graphData"));
            setIsGraphDataLoading(false);
        }

    }

    const handleSortTable = (direction: 'asc' | 'desc', columnDisplayName: string,) => {
        setSorting(true);
        setSortConfig({ key: columnDisplayName, direction });
        const presenceData = mbptableData?.find((item: any) => (item.presence_type == selectedPresenceType))?.presence_data;
        if (presenceData && presenceData.length > 0) {
            // Map display name to actual data key
            const columnKey = (() => {
                switch (columnDisplayName) {
                    case 'Name':
                        return 'presence_name';
                    case 'Status':
                        return 'start_date';
                    case 'State':
                    case 'District':
                    case 'Subdistrict':
                        return 'geoname1';
                    case 'Pincode':
                        return 'geoname2';
                    default:
                        return columnDisplayName;
                }
            })();

            const parseValue = (value: string | number | null | undefined): number | string | null => {
                if (value === null || value === undefined || value === '' || value === 'N/A') return null;
                if (typeof value === 'number') return value;
                if (typeof value === 'string') {
                    const parsed = parseFloat(value.replace(/,/g, '').trim());
                    return !isNaN(parsed) ? parsed : value.trim();
                }
                return null;
            };

            const sortedData = [...presenceData].sort((a, b) => {
                const parsedA = parseValue(a[columnKey]);
                const parsedB = parseValue(b[columnKey]);

                if (parsedA === null && parsedB === null) return 0;
                if (parsedA === null) return 1;
                if (parsedB === null) return -1;

                if (typeof parsedA === 'number' && typeof parsedB === 'number') {
                    return direction === 'asc' ? parsedA - parsedB : parsedB - parsedA;
                } else if (typeof parsedA === 'string' && typeof parsedB === 'string') {
                    return direction === 'asc'
                        ? parsedA.localeCompare(parsedB)
                        : parsedB.localeCompare(parsedA);
                } else if (typeof parsedA === 'number') {
                    return direction === 'asc' ? -1 : 1;
                } else {
                    return direction === 'asc' ? 1 : -1;
                }
            });
            const updatedData = mbptableData.map((item: any) =>
                item.presence_type === selectedPresenceType
                    ? { ...item, presence_data: sortedData }
                    : item
            );

            setMbptableData(updatedData);
            setSelectedRowData(updatedData?.find((item: any) => (item.presence_type == selectedPresenceType))?.presence_data[0]);
        }
    };

    useEffect(() => {
        fetchDropDownData();
    }, [geoCode, selectedCardType.mbpData]);

    useEffect(() => {
        if (selectedfilter && selectedCardType.mbpData) {
            fetchMbpTableData();
        } else {
            setMbptableData([]);
        }
    }, [selectedfilter, selectedCardType.mbpData, geoCode]);

    useEffect(() => {
        if (geoCode && selectedfilter && selectedRowData?.presence_id) {
            setGraphData({});
            setIsGraphDataLoading(true);
            setShimmerLite((prev: string[]) => [...prev, "graphData"]);

            fetchGraphData();
        }
    }, [geoCode, selectedfilter, selectedRowData?.presence_id]);

    useEffect(() => {
        if (mbptableData && mbptableData.length > 0) {
            setSelectedRowData(mbptableData?.find((item: any) => (item.presence_type == selectedPresenceType))?.presence_data[0]);
        }
    }, [selectedPresenceType, mbptableData]);

    useEffect(() => {
        if (mbptableData && mbptableData.length > 0 && !sorting) {
            handleSortTable("asc", "Name");
        }
    }, [mbptableData, sorting]);

    useEffect(() => {
        if (mbptableData && mbptableData.length > 0) {
            handleSelectedRow();
        }
    }, [mbptableData]);

    return (
        <div style={{ padding: '2.8125vh 2.8125vh 0 2.8125vh' }} className='w-100 d-flex h-100 flex-column justify-content-start align-items-start'
        >
            {shimmerLite.includes('dataIsLoading')
                ? <div className="w-100 d-flex flex-column justify-content-start align-items-start position-relative">
                    {/* Title */}
                    <div className='w-100 position-relative' style={{ height: '2.5vh', marginBottom: '1.5vh' }}>
                        <div className="shimmer position-absolute" style={{ width: '40%', borderRadius: '4px' }}></div>
                    </div>

                    {/* Subtitle */}
                    <div className='w-100 position-relative' style={{ height: '1.8vh', marginBottom: '2vh' }}>
                        <div className="shimmer position-absolute" style={{ width: '50%', borderRadius: '4px' }}></div>
                    </div>

                    {/* Table Header Shimmer */}
                    <div className='w-100 position-relative' style={{ height: '3.774vh', marginBottom: '2vh' }}>
                        <div className="shimmer position-absolute" style={{ width: '60%', borderRadius: '4px' }}></div>
                    </div>
                    <div className="w-100 height-56-45vh mb-2 row">
                        {/* Table Shimmer */}
                        <div className='col-6 position-relative h-100'>
                            <TableSkeletonLoader rows={8} />
                        </div>
                        {/* Graph Shimmer */}
                        <div className='col-6 position-relative h-100'>
                            <div className="shimmer position-absolute" style={{ width: '100%', borderRadius: '8px' }}></div>
                        </div>
                    </div>
                </div>
                : <div className="w-100 d-flex flex-column justify-content-start align-items-start position-relative">
                    <Heading
                        title='Insights on My Business Presence'
                        type={TypographyType.h3}
                        colour={TypographyColor.primary}
                        classname='text-start text-truncate'
                    />
                    <Body
                        type={BodyType.p4}
                        color={BodyColor.secondary}
                        classname="text-start text-truncate margin-top-2 margin-bottom-5"
                    >
                        Explore detailed insights into your business performance and growth.
                    </Body>
                    {shimmerLite.includes('mbpTableData')
                        ? <div className="w-100 height-56-45vh mb-2 row">
                            {/* Table Shimmer */}
                            <div className='col-6 position-relative h-100'>
                                <TableSkeletonLoader rows={8} />
                            </div>
                            {/* Graph Shimmer */}
                            <div className='col-6 position-relative h-100'>
                                <div className="shimmer position-absolute" style={{ width: '100%', borderRadius: '8px' }}></div>
                            </div>
                        </div>
                        : (mbptableData?.length === 0 && dropDownData?.length === 0) && !isBarGraphLoading && shimmerLite?.length === 0
                            ? <div className='w-100 height-73-45vh padding-top-5'><NoPoi /></div>
                            : <div className='w-100 h-100'>
                                <div className="d-flex flex-row justify-content-between align-items-center w-100 margin-bottom-5">
                                    {/* Left: Presence types */}
                                    <div className="d-flex flex-row gap-4 align-items-center">
                                        {mbptableData && mbptableData?.map((item: any, index: number) => (
                                            <div key={`presence-${index}`} className="d-flex flex-column align-items-center">
                                                <div className="d-flex flex-row align-items-center gap-1 cursor-pointer"
                                                    onClick={() => handlePresenceTypeChange(item, index)}>
                                                    <img
                                                        src={item?.icon}
                                                        alt={item?.presence_type}
                                                        className="presence-type-icon"
                                                        title={`(${item?.presence_type})`}
                                                    />
                                                    <Heading
                                                        title={`${item.presence_type} (${item.count})`}
                                                        type={TypographyType.h5}
                                                        colour={selectedPresenceType === item.presence_type ? TypographyColor.primary : TypographyColor.secondary}
                                                        classname="text-truncate"
                                                    />
                                                </div>
                                                {selectedPresenceType === item.presence_type && (
                                                    <div className="w-100" style={{ borderBottom: '2px solid black', marginTop: '4px' }} />
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Right: Dropdown */}
                                    {dropDownData.length > 0 &&
                                        <CustomDropdown
                                            options={dropDownData.map(item => item)}
                                            selectedValue={selectedfilter}
                                            onSelect={(value: any) => {
                                                setSelectedfilter(value)
                                                setIsfilterChange(true)
                                            }}
                                            labelKey="code"
                                            valueKey="code"
                                            name="code"
                                            classname="custom-dropdown"
                                            style={{ width: '14vw' }} // adjust as needed
                                        />
                                    }
                                </div>
                                {mbptableData &&
                                    <div>
                                        <div className='d-flex gap-3 margin-top-2' style={{ height: '44.268vh' }}>
                                            <div className="custom-table-container horizontal-scroll">
                                                <table className="custom-table">
                                                    <thead>
                                                        <tr>
                                                            {mbptableData && mbptableData?.find((item: any) => (item.presence_type == selectedPresenceType))?.header_data?.map((header: string) => (
                                                                <th key={header}>
                                                                    <div className="d-flex flex-row align-items-center justify-content-between">
                                                                        <Heading
                                                                            title={header}
                                                                            type={TypographyType.h5}
                                                                            colour={TypographyColor.secondary}
                                                                            classname="text-truncate"
                                                                        />
                                                                        <div className="d-flex flex-column">
                                                                            <FaAngleUp
                                                                                className='fs-12'
                                                                                color={sortConfig?.key === header && sortConfig?.direction === "asc" ? "#2B0D5C" : "#939393"}
                                                                                onClick={() => handleSortTable("asc", header)}
                                                                            />
                                                                            <FaAngleDown
                                                                                className='fs-12'
                                                                                color={sortConfig?.key === header && sortConfig?.direction === "desc" ? "#2B0D5C" : "#939393"}
                                                                                onClick={() => handleSortTable("desc", header)}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(mbptableData && mbptableData?.length > 0 && mbptableData?.find((item: any) => (item.presence_type == selectedPresenceType)).presence_data.length > 0) ?
                                                            mbptableData && mbptableData?.find((item: any) => (item.presence_type == selectedPresenceType))?.presence_data?.map((row: any, index: number) => (
                                                                <React.Fragment key={index}>
                                                                    <tr
                                                                        key={row.id}
                                                                        ref={el => rowRefs.current[index] = el}
                                                                        className={`${(selectedRowIndex === index && childSelectedRowIndex !== -1) ? 'child-row' : (selectedRowIndex === index && childSelectedRowIndex === -1) ? 'childrowSelected' : 'nonSelectedRow'}`}
                                                                        onClick={() => {
                                                                            setSelectedRowData(row);
                                                                            setSelectedRowIndex(index);
                                                                            setChildSelectedRowIndex(-1);
                                                                        }}
                                                                        tabIndex={0}
                                                                    >
                                                                        <td className='position-sticky'
                                                                            onMouseEnter={(e) => handleMouseEnter(e, row?.presence_name)}
                                                                            onMouseLeave={handleMouseLeave}>
                                                                             <div className='d-flex gap-2 align-items-center justify-content-between single-poi-info-cardsss position-relative'>
                                                                                 <div className='w-100 d-flex flex-row gap-2 align-items-center'>
                                                                                     <img src={row.icon} alt={row?.presence_name} className='presence-icon' />
                                                                                     <Body
                                                                                         type={BodyType.p2}
                                                                                         color={BodyColor.secondary}
                                                                                         classname={`ellipsis-text ${row?.isCatchment ? 'text-decoration-underline cursor-pointer' : ''}`}
                                                                                         onClick={row?.isCatchment ? (e: React.MouseEvent) => {
                                                                                             e.stopPropagation();
                                                                                             handleCatchmentClick(row.catchmentId);
                                                                                         } : undefined}
                                                                                     >
                                                                                         {row?.presence_name}
                                                                                     </Body>
                                                                                 </div>
                                                                                 <span
                                                                                     className='material-symbols-outlined fs-18 cursor-pointer fw-bold color-black-5'
                                                                                     onClick={() => {
                                                                                         toggleExpand(index, row)
                                                                                         setSelectedRowIndex(index);
                                                                                         setChildSelectedRowIndex(-1);
                                                                                     }}
                                                                                 >
                                                                                     {row.associated_presence?.length > 0 && (expandedRowIndex === index && selectedRowIndex === index ? 'keyboard_arrow_up' : 'keyboard_arrow_down')}
                                                                                 </span>
                                                                             </div>
                                                                         </td>
                                                                         <td>
                                                                             <div className='w-100 d-flex justify-content-between'>
                                                                                 <Body
                                                                                     type={BodyType.p2}
                                                                                     color={BodyColor.secondary}
                                                                                     classname="text-truncate"
                                                                                 >
                                                                                     {`Active : ${row?.start_date} - ${row?.end_date}`}
                                                                                 </Body>
                                                                             </div>
                                                                         </td>
                                                                         <td
                                                                             onMouseEnter={(e) => handleMouseEnter(e, row?.geoname1 ?? 'N/A')}
                                                                             onMouseLeave={handleMouseLeave}
                                                                         >
                                                                             <Body
                                                                                 type={BodyType.p2}
                                                                                 color={BodyColor.secondary}
                                                                                 classname="text-truncate"
                                                                             >
                                                                                 {row?.geoname1 || 'N/A'}
                                                                             </Body>
                                                                         </td>
                                                                         {row.geoname2 && <td
                                                                             onMouseEnter={(e) => handleMouseEnter(e, row?.geoname2 ?? 'N/A')}
                                                                             onMouseLeave={handleMouseLeave}
                                                                         >
                                                                             <Body
                                                                                 type={BodyType.p2}
                                                                                 color={BodyColor.secondary}
                                                                                 classname="text-truncate"
                                                                             >
                                                                                 {row?.geoname2 || 'N/A'}
                                                                             </Body>
                                                                         </td>}
                                                                     </tr>
                                                                     {expandedRowIndex === index && selectedRowIndex === index &&
                                                                         row.associated_presence?.map((child: any, childIndex: number) => (
                                                                             <tr key={`${index}-${childIndex}`}
                                                                                 className={`child-row ${childSelectedRowIndex === childIndex && selectedRowIndex === index
                                                                                     ? "child-selected"
                                                                                     : ""
                                                                                     }`}
                                                                                 onClick={() => {
                                                                                     setSelectedRowData(child);
                                                                                     setSelectedRowIndex(index);
                                                                                     setChildSelectedRowIndex(childIndex);

                                                                                 }}
                                                                             >
                                                                                <td className='position-sticky'
                                                                                    onMouseEnter={(e) => handleMouseEnter(e, child?.presence_name)}
                                                                                    onMouseLeave={handleMouseLeave}
                                                                                >
                                                                                     <div className='d-flex gap-2 align-items-center ms-5 single-poi-info-cardsss position-relative'>
                                                                                         <img src={child?.icon} alt={child?.presence_name} className='presence-icon' />
                                                                                         <Body
                                                                                             type={BodyType.p2}
                                                                                             color={BodyColor.secondary}
                                                                                             classname={`text-truncate ${child?.isCatchment ? 'text-decoration-underline cursor-pointer' : ''}`}
                                                                                             onClick={child?.isCatchment ? (e: React.MouseEvent) => {
                                                                                                 e.stopPropagation();
                                                                                                 handleCatchmentClick(child.catchmentId);
                                                                                             } : undefined}
                                                                                         >
                                                                                             {`Associated ${child?.presence_name}`}
                                                                                         </Body>

                                                                                     </div>
                                                                                 </td>
                                                                                <td>
                                                                                    <Body
                                                                                        type={BodyType.p2}
                                                                                        color={BodyColor.secondary}
                                                                                        classname="text-truncate"
                                                                                    >
                                                                                        {`Active : ${child?.start_date} - ${child?.end_date}`}
                                                                                    </Body>
                                                                                </td>
                                                                                <td
                                                                                    onMouseEnter={(e) => handleMouseEnter(e, child?.geoname1 ?? 'N/A')}
                                                                                    onMouseLeave={handleMouseLeave}
                                                                                >
                                                                                    <Body
                                                                                        type={BodyType.p2}
                                                                                        color={BodyColor.secondary}
                                                                                        classname="text-truncate"
                                                                                    >
                                                                                        {child?.geoname1 || 'N/A'}
                                                                                    </Body>
                                                                                </td>
                                                                                {child.geoname2 && <td
                                                                                    onMouseEnter={(e) => handleMouseEnter(e, child?.geoname2 ?? 'N/A')}
                                                                                    onMouseLeave={handleMouseLeave}
                                                                                >
                                                                                    <Body
                                                                                        type={BodyType.p2}
                                                                                        color={BodyColor.secondary}
                                                                                        classname="text-truncate"
                                                                                    >
                                                                                        {child?.geoname2 || 'N/A'}
                                                                                    </Body>
                                                                                </td>}
                                                                             </tr>
                                                                         ))
                                                                     }
                                                                 </React.Fragment>
                                                            ))
                                                            : shimmerLite?.length==0 &&  <tr>
                                                                <td colSpan={3} style={{ textAlign: "center" }}>
                                                                    No data found
                                                                </td>
                                                            </tr>
                                                        }
                                                    </tbody>
                                                </table>
                                            </div>
                                            {tooltip.show && (
                                                <div
                                                    style={{
                                                        position: "fixed",
                                                        top: tooltip.y,
                                                        left: tooltip.x,
                                                        padding: "5px 8px",
                                                        zIndex: 9999
                                                    }}
                                                    className='helper-highlight-text-mbp'
                                                >
                                                    {tooltip.text}
                                                </div>
                                            )}

                                            <div className="my-business-presence-chart">
                                                {(shimmerLite.length>0 || isGraphDataLoading)
                                                    ? <div className="h-100 w-100 d-flex flex-column justify-content-start align-items-center position-relative">
                                                        <div className="shimmer position-absolute" style={{ width: '100%', borderRadius: '8px' }}></div>
                                                    </div>
                                                    : <div className="h-100 w-100 d-flex flex-column justify-content-start align-items-center">
                                                        <div className="w-100 padding-top-bottom-3 padding-left-right-4 bottom-border d-flex flex-row justify-content-start align-items-center" style={{ height: '16%' }}>
                                                            <img
                                                                src={selectedRowData?.icon}
                                                                alt={selectedRowData?.presence_type}
                                                                className="presence-icon me-1"
                                                            />
                                                            <Heading
                                                                title={selectedRowData?.presence_name}
                                                                type={TypographyType.h5}
                                                                colour={TypographyColor.secondary}
                                                                classname='text-start text-truncate'
                                                            />&nbsp;
                                                            <Heading
                                                                title={`(Active: ${selectedRowData?.start_date} - ${selectedRowData?.end_date})`}
                                                                type={TypographyType.h6}
                                                                colour={TypographyColor.secondary}
                                                                classname='text-start text-truncate'
                                                            />
                                                        </div>
                                                        {
                                                            !isGraphDataLoading && Object.keys(graphData).length == 0
                                                                ? <div className='w-100 h-100 d-flex flex-row justify-content-center align-items-center'>
                                                                    <div className="noDataBackgroundImage" style={{ width: '33vh', height: '33vh' }}>
                                                                        <div className='position-absolute d-flex flex-column justify-content-center align-items-center' style={{ top: '62%' }}>
                                                                            <Heading
                                                                                title='Data not provided'
                                                                                type={TypographyType.h4}
                                                                                colour={TypographyColor.primary}
                                                                                classname='margin-bottom-5'
                                                                            />
                                                                            <Heading
                                                                                title='Unlock richer insights into your business presence by sharing your data with the EI team.'
                                                                                type={TypographyType.h5}
                                                                                colour={TypographyColor.secondary}
                                                                                classname='text-center margin-bottom-5'
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                : !isGraphDataLoading && Object.keys(graphData).length > 0 && <BranchGraph graphData={graphData} />
                                                        }
                                                        {Object.keys(graphData).length > 0 &&
                                                            graphData.x_axis.some((year: any) => year.includes("*")) && (
                                                                <div className="w-100 d-flex flex-row justify-content-end padding-right-3">
                                                                    <Heading
                                                                        title='* We have no data for this year'
                                                                        type={TypographyType.h7}
                                                                        colour={TypographyColor.secondary}
                                                                        classname='text-start'
                                                                    />
                                                                </div>)}
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                        {mbptableData && mbptableData?.length > 0 && mbptableData?.find((item: any) => (item.presence_type == selectedPresenceType))?.footer_data?.length > 0 &&
                                            <div className="w-100 d-flex flex-row justify-content-center margin-top-5">
                                                <div className='width-85 d-flex flex-row align-items-center justify-content-between' >
                                                    {
                                                        mbptableData &&
                                                        mbptableData?.find((item: any) => (item.presence_type == selectedPresenceType))?.footer_data.map((footerItem: any, index: number) => (
                                                            <div key={`footer-${index}`} className='d-flex flex-row gap-1 align-items-center'>
                                                                <div className='d-flex flex-row align-items-center gap-2'>
                                                                    <Heading
                                                                        title={`${footerItem.type} :`}
                                                                        type={TypographyType.h6}
                                                                        colour={TypographyColor.secondary}
                                                                        classname="text-start text-truncate"
                                                                    />
                                                                    <div className='d-flex flex-row gap-3 justify-content-center align-items-center' >
                                                                        {footerItem.values.map((item: any, i: number) => (
                                                                            <div key={`footer-item-${index}-${i}`} className="d-flex flex-row align-items-center gap-1">
                                                                                <img
                                                                                    src={item.icon}
                                                                                    alt={item.presence_type}
                                                                                    className={` ${footerItem.type === 'Performance' ? 'presence-type-icon' : 'presence-growth-icon'}`}
                                                                                />
                                                                                <Heading
                                                                                    title={item.text}
                                                                                    type={TypographyType.h6}
                                                                                    colour={TypographyColor.secondary}
                                                                                    classname=""
                                                                                />
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))
                                                    }
                                                    <div key={`footer-2`} className="d-flex flex-row align-items-center gap-1">
                                                        <Heading
                                                            title='- - -'
                                                            type={TypographyType.h6}
                                                            colour={TypographyColor.secondary}
                                                            classname=""
                                                        />
                                                        &nbsp;
                                                        <Heading
                                                            title='Most Frequent Value'
                                                            type={TypographyType.h6}
                                                            colour={TypographyColor.secondary}
                                                            classname=""
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                    </div>
                                }
                            </div>
                    }
                </div>
            }
        </div>
    );
};